import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type UseFormReturn, useForm } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject, useToast } from '@kanda-libs/ks-design-library';
import { Company } from '@kanda-libs/ks-frontend-services';

import { APP_ENV } from 'common/constants';
import { selectors } from 'store';

import { extractAprs } from './helpers';

import { BASE_URL_PROD, BASE_URL_QA } from '../constants';

export interface FinanceCalculatorLinkModalFormValues {
  excludeAprs: string[];
}

export interface FinanceCalculatorLinkModalFormHook {
  form: UseFormReturn<FinanceCalculatorLinkModalFormValues>;
  aprs: string[];
  url: string | undefined;
  onClick: () => void;
}

export default function useFinanceCalculatorLinkModalForm(): FinanceCalculatorLinkModalFormHook {
  const company = useSelector(selectors.company.getEntity);
  const rates = useMemo(() => company?.finance_rates, [company]);
  const { showSuccess } = useToast();

  const aprs = useMemo(() => {
    if (!rates) return [];
    return extractAprs(rates);
  }, [rates]);

  const cid = useMemo(() => company?.cid, [company]);

  const iar = useMemo(() => {
    const name =
      company?.contact_info?.trading_name ||
      company?.limited_company_info?.company_name ||
      company?.sole_trader_info?.trading_name;
    if (!name) return undefined;
    return encodeURIComponent(name);
  }, [company]);

  const form = useForm<FinanceCalculatorLinkModalFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      excludeAprs: [],
    },
  });

  const excludeAprs = form.watch('excludeAprs');

  const url = useMemo(() => {
    if (!cid) return undefined;
    const base = APP_ENV === 'qa' ? BASE_URL_QA : BASE_URL_PROD;
    const suffix = [
      `cid=${cid}`,
      iar ? `iar=${iar}` : undefined,
      excludeAprs.length === 0
        ? undefined
        : `excludeAprs=${excludeAprs.join(',')}`,
    ]
      .filter(Boolean)
      .join('&');
    return `${base}?${suffix}`;
  }, [cid, iar, excludeAprs]);

  const onClick = useCallback(() => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    showSuccess('URL copied');
  }, [url]);

  return {
    form,
    aprs,
    url,
    onClick,
  };
}
